import { ColorSwatch, Flex, Text, Title } from "@mantine/core";
import {
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PDFPage } from "./PDFPage";
import useReportStore from "../../../store/useReportStore";

const COLORS = [
  "#05D8D2",
  "#A64DD7",
  "#6F40E2",
  "#3A8BD9",
  "#FF5733",
  "#DAF7A6",
];

export const TrendingSkillsPDF = () => {
  const { report } = useReportStore();
  const content = report.skills_trending || [];

  const skills = content.data.map((skill, index) => ({
    name: skill.title,
    color: COLORS[index % COLORS.length],
    description: skill.description,
    trending: skill.trending,
  }));

  const allTrendingValues = skills.flatMap((skill) =>
    Object.values(skill.trending)
  );

  const minValue = Math.min(...allTrendingValues);
  const maxValue = Math.max(...allTrendingValues);

  return (
    <PDFPage padding>
      <Title size={48} mb={8}>
        {content?.mark_down ? content?.mark_down.replace("##", "") : " "}
      </Title>
      <Text mb={44} size="28px" lh="42px" c="dimmed">
        {content?.explanation ? content?.explanation : ""}
      </Text>
      <ResponsiveContainer width="100%" height={625}>
        <AreaChart
          data={Object.keys(skills[0]?.trending || {}).map((year) => ({
            year,
            ...skills.reduce(
              (acc, skill) => ({ ...acc, [skill.name]: skill.trending[year] }),
              {}
            ),
          }))}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            {skills.map((skill, index) => (
              <linearGradient
                key={index}
                id={`color${index + 1}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={skill.color} stopOpacity={0.8} />
                <stop
                  offset="95%"
                  stopColor={`${skill.color}33`}
                  stopOpacity={0}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis
            domain={[minValue, maxValue]}
            ticks={[
              minValue,
              maxValue / 4,
              maxValue / 2,
              (maxValue * 3) / 4,
              maxValue,
            ]}
          />
          <Tooltip />
          {skills.map((skill, index) => (
            <Area
              key={index}
              type="linear"
              dataKey={skill.name}
              stroke={skill.color}
              strokeWidth={3}
              fill={`url(#color${index + 1})`}
              fillOpacity={1}
            />
          ))}
          {skills.map((skill, index) => (
            <Line
              key={index}
              type="linear"
              dataKey={skill.name}
              stroke={skill.color}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      {skills.map((skill, index) => (
        <Flex key={index} gap={16} mt={16}>
          <ColorSwatch color={skill.color} size={24} radius={2} mt={10} />
          <Text size="24px" lh="38px">
            <b>{skill.name}</b>: {skill.description}
          </Text>
        </Flex>
      ))}
    </PDFPage>
  );
};
